import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import {
  claveDatosPago,
  TarjetaDatoPago,
  useDatosPago,
} from "@/componentes/catalogos/DatosParaPagar"
import {
  CASILLAS,
  type CampoDatoPago,
  type DatoPago,
  NOMBRE_TIPO,
  TIPOS,
  type TipoDatoPago,
} from "@/componentes/catalogos/datosPago"
import type { Banco } from "@/componentes/tesoreria/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

type Valores = Partial<Record<CampoDatoPago, string>>

const soloDigitos = (texto: string) => texto.replace(/\D/g, "")

/**
 * «Datos de pago» en la ficha del proveedor (03/11, pedido del usuario): sus cuentas,
 * pago móvil, Zelle, Binance o billeteras, para que tesorería los vea al pagar.
 *
 * ⚠ **Se cargan A MANO, aquí y en ningún otro sitio.** El lector de solicitudes de pago
 * deja los datos del mensaje en las NOTAS de la orden y no en la ficha: un mensaje puede
 * traer la cuenta de un tercero (decisión del usuario del 28/09).
 */
export function DatosDePagoProveedor({ proveedorId }: { proveedorId: number }) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "proveedor:administrar")
  const consulta = useDatosPago(proveedorId)
  const [editando, setEditando] = useState<DatoPago | "nuevo" | null>(null)
  const [borrando, setBorrando] = useState<number | null>(null)

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/proveedores/${proveedorId}/datos-pago/${id}`),
    onSuccess: () => {
      setBorrando(null)
      clienteQuery.invalidateQueries({ queryKey: claveDatosPago(proveedorId) })
    },
  })

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Datos de pago</h3>
        {puedeEditar && editando === null && (
          <Boton tamano="sm" onClick={() => setEditando("nuevo")}>
            <Plus size={14} aria-hidden={true} /> Agregar
          </Boton>
        )}
      </div>

      {editando !== null && (
        <FormularioDatoPago
          proveedorId={proveedorId}
          dato={editando === "nuevo" ? null : editando}
          onListo={() => setEditando(null)}
        />
      )}

      {consulta.isPending && <EstadoCarga etiqueta="Cargando los datos de pago…" filas={2} />}
      {consulta.isError && (
        <EstadoError
          titulo="No se pudieron cargar los datos de pago."
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
        />
      )}
      {consulta.data && consulta.data.datos.length === 0 && editando === null && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no tiene. Agrega su cuenta, pago móvil, Zelle o Binance y tesorería los verá al
          pagarle.
        </p>
      )}
      <div className="grid gap-2">
        {consulta.data?.datos.map((dato) => (
          <TarjetaDatoPago
            key={dato.id}
            dato={dato}
            acciones={
              puedeEditar &&
              editando === null &&
              (borrando === dato.id ? (
                <span className="flex items-center gap-1 text-xs">
                  ¿Eliminar?
                  <Boton
                    tamano="sm"
                    variante="peligro"
                    disabled={eliminar.isPending}
                    onClick={() => eliminar.mutate(dato.id)}
                  >
                    Sí, eliminar
                  </Boton>
                  <Boton tamano="sm" onClick={() => setBorrando(null)}>
                    No
                  </Boton>
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Editar ${dato.titulo}`}
                    onClick={() => setEditando(dato)}
                    className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                  >
                    <Pencil size={14} aria-hidden={true} />
                  </button>
                  <button
                    type="button"
                    aria-label={`Eliminar ${dato.titulo}`}
                    onClick={() => setBorrando(dato.id)}
                    className="foco-cristal toque-cristal inline-flex size-8 items-center justify-center rounded-[var(--radius-control)]"
                  >
                    <Trash2 size={14} aria-hidden={true} />
                  </button>
                </span>
              ))
            }
          />
        ))}
      </div>
      {eliminar.error instanceof ErrorApi && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {eliminar.error.message}
        </p>
      )}
    </section>
  )
}

/**
 * Alta o edición de un dato. Cada clase enseña sus casillas (`CASILLAS`); lo que exige
 * cada una lo vuelve a decidir el servidor, que además limpia lo que no le corresponde.
 *
 * ⚠ **El banco de una cuenta venezolana se propone desde el número**: los cuatro primeros
 * dígitos son su código. Se puede cambiar, y el servidor rechaza un banco que no cuadre.
 */
function FormularioDatoPago({
  proveedorId,
  dato,
  onListo,
}: {
  proveedorId: number
  dato: DatoPago | null
  onListo: () => void
}) {
  const clienteQuery = useQueryClient()
  const id = useId()
  const [tipo, setTipo] = useState<TipoDatoPago>(dato?.tipo ?? "CUENTA_NACIONAL")
  const [valores, setValores] = useState<Valores>(() => {
    const inicial: Valores = {}
    if (dato) {
      for (const casilla of Object.values(CASILLAS).flat()) {
        const valor = dato[casilla.campo]
        if (valor !== null && valor !== undefined) inicial[casilla.campo] = String(valor)
      }
      if (dato.notas) inicial.notas = dato.notas
    }
    return inicial
  })
  const [preferido, setPreferido] = useState(dato?.preferido ?? false)

  // La misma clave que el formulario de cuentas de Tesorería: comparten la caché.
  const bancos = useQuery({
    queryKey: ["bancos", "activos"],
    queryFn: () => api.get<Banco[]>("/api/v1/bancos?solo_activos=true"),
  })

  function poner(campo: CampoDatoPago, valor: string) {
    setValores((v) => {
      const siguiente = { ...v, [campo]: valor }
      if (campo === "numero_cuenta" && tipo === "CUENTA_NACIONAL") {
        const codigo = soloDigitos(valor).slice(0, 4)
        const banco = bancos.data?.find((b) => b.codigo === codigo)
        if (banco) siguiente.banco_id = String(banco.id)
      }
      return siguiente
    })
  }

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo: Record<string, unknown> = { tipo, preferido }
      for (const [campo, valor] of Object.entries(valores)) {
        const limpio = (valor ?? "").trim()
        cuerpo[campo] = campo === "banco_id" ? (limpio ? Number(limpio) : null) : limpio || null
      }
      return dato
        ? api.put(`/api/v1/proveedores/${proveedorId}/datos-pago/${dato.id}`, cuerpo)
        : api.post(`/api/v1/proveedores/${proveedorId}/datos-pago`, cuerpo)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: claveDatosPago(proveedorId) })
      onListo()
    },
  })

  return (
    <form
      aria-label={dato ? "Editar dato de pago" : "Nuevo dato de pago"}
      className="tarjeta space-y-3 p-3"
      onSubmit={(evento) => {
        evento.preventDefault()
        guardar.mutate()
      }}
    >
      <label className="block text-sm" htmlFor={`${id}-tipo`}>
        Qué es
        <select
          id={`${id}-tipo`}
          className="campo mt-1"
          value={tipo}
          onChange={(e) => setTipo(e.target.value as TipoDatoPago)}
        >
          {TIPOS.map((t) => (
            <option key={t} value={t}>
              {NOMBRE_TIPO[t]}
            </option>
          ))}
        </select>
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        {CASILLAS[tipo].map((casilla) => {
          const campoId = `${id}-${casilla.campo}`
          const valor = valores[casilla.campo] ?? ""
          return (
            <label
              key={casilla.campo}
              className={`block text-sm ${casilla.ancha ? "sm:col-span-2" : ""}`}
              htmlFor={campoId}
            >
              {casilla.etiqueta}
              {casilla.obligatorio && <span aria-hidden={true}> *</span>}
              {casilla.campo === "banco_id" ? (
                <select
                  id={campoId}
                  className="campo mt-1"
                  value={valor}
                  onChange={(e) => poner("banco_id", e.target.value)}
                >
                  <option value="">
                    {tipo === "CUENTA_NACIONAL" ? "El del número" : "Elige el banco"}
                  </option>
                  {bancos.data?.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.codigo ? `${b.codigo} · ` : ""}
                      {b.nombre}
                    </option>
                  ))}
                </select>
              ) : casilla.campo === "tipo_cuenta" ? (
                <select
                  id={campoId}
                  className="campo mt-1"
                  value={valor}
                  onChange={(e) => poner("tipo_cuenta", e.target.value)}
                >
                  <option value="">Sin indicar</option>
                  <option value="CORRIENTE">Corriente</option>
                  <option value="AHORRO">Ahorro</option>
                </select>
              ) : (
                <input
                  id={campoId}
                  className={`campo mt-1 ${casilla.numerica ? "tabular-nums" : ""}`}
                  inputMode={casilla.numerica ? "numeric" : undefined}
                  value={valor}
                  onChange={(e) => poner(casilla.campo, e.target.value)}
                />
              )}
              {casilla.ayuda && (
                <span className="mt-0.5 block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {casilla.ayuda}
                </span>
              )}
            </label>
          )
        })}
        <label className="block text-sm sm:col-span-2" htmlFor={`${id}-notas`}>
          Notas
          <input
            id={`${id}-notas`}
            className="campo mt-1"
            value={valores.notas ?? ""}
            onChange={(e) => poner("notas", e.target.value)}
            placeholder="Sólo para fletes, pedir confirmación antes…"
          />
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={preferido}
          onChange={(e) => setPreferido(e.target.checked)}
        />
        El preferido para pagarle
      </label>

      {guardar.error instanceof ErrorApi && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error.message}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Boton type="submit" variante="primario" tamano="sm" disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
        <Boton tamano="sm" onClick={onListo} disabled={guardar.isPending}>
          Cancelar
        </Boton>
      </div>
    </form>
  )
}
